import React, { useState, useEffect } from "react";

const CustomAudienceModal = ({ open, onOpenChange, onSave, editingAudience }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    ageRange: "",
    interests: "",
    demographics: "",
    painPoints: "",
    goals: "",
    location: "",
    income: "",
    education: "",
  });

  const [interestTags, setInterestTags] = useState([]);
  const [currentInterest, setCurrentInterest] = useState("");

  useEffect(() => {
    if (editingAudience) {
      setFormData({
        title: editingAudience.title || "",
        description: editingAudience.description || "",
        ageRange: editingAudience.details?.ageRange || "",
        interests: editingAudience.interests || "",
        demographics: editingAudience.demographics || "",
        painPoints: editingAudience.details?.painPoints || "",
        goals: editingAudience.details?.goals || "",
        location: editingAudience.details?.location || "",
        income: editingAudience.details?.income || "",
        education: editingAudience.details?.education || "",
      });
      setInterestTags(editingAudience.details?.interests || []);
    }
  }, [editingAudience]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addInterestTag = () => {
    if (currentInterest.trim() && !interestTags.includes(currentInterest.trim())) {
      setInterestTags((prev) => [...prev, currentInterest.trim()]);
      setCurrentInterest("");
    }
  };

  const removeInterestTag = (tag) => {
    setInterestTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addInterestTag();
    }
  };

  const handleSave = () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }

    const audience = {
      id: `custom-${Date.now()}`,
      title: formData.title,
      description: formData.description,
      demographics: `${formData.ageRange}${formData.location ? ` • ${formData.location}` : ""}${formData.income ? ` • ${formData.income}` : ""}`,
      isCustom: true,
      details: {
        ...formData,
        interests: interestTags,
      },
    };

    onSave(audience);

    // Reset form
    setFormData({
      title: "",
      description: "",
      ageRange: "",
      interests: "",
      demographics: "",
      painPoints: "",
      goals: "",
      location: "",
      income: "",
      education: "",
    });
    setInterestTags([]);
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99]">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {editingAudience ? "Edit Audience" : "Create Custom Audience"}
              </h2>
              <p className="text-gray-600 mt-1">
                Define your target audience with specific demographics, interests, and characteristics.
              </p>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Basic Information</h4>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Audience Name *</label>
              <input
                type="text"
                placeholder="e.g., Tech-Savvy Millennials"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Description *</label>
              <textarea
                placeholder="Brief description of this audience segment"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                className="min-h-[80px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Demographics */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Demographics</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Age Range</label>
                <input
                  type="text"
                  placeholder="e.g., 25-35 years"
                  value={formData.ageRange}
                  onChange={(e) => handleInputChange("ageRange", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  placeholder="e.g., Urban areas, US"
                  value={formData.location}
                  onChange={(e) => handleInputChange("location", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Income Level</label>
                <input
                  type="text"
                  placeholder="e.g., Middle to high income"
                  value={formData.income}
                  onChange={(e) => handleInputChange("income", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Education Level</label>
                <input
                  type="text"
                  placeholder="e.g., College educated"
                  value={formData.education}
                  onChange={(e) => handleInputChange("education", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Interests */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Interests & Behaviors</h4>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Interests</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add an interest (press Enter)"
                    value={currentInterest}
                    onChange={(e) => setCurrentInterest(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <button
                    onClick={addInterestTag}
                    className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600"
                  >
                    Add
                  </button>
                </div>
                {interestTags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {interestTags.map((tag, index) => (
                      <span
                        key={index}
                        className="bg-orange-100 text-orange-800 px-2 py-1 rounded-md text-sm flex items-center gap-1"
                      >
                        {tag}
                        <button
                          onClick={() => removeInterestTag(tag)}
                          className="text-orange-600 hover:text-orange-800"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Pain Points</label>
              <textarea
                placeholder="What problems or challenges does this audience face?"
                value={formData.painPoints}
                onChange={(e) => handleInputChange("painPoints", e.target.value)}
                className="min-h-[80px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Goals & Aspirations</label>
              <textarea
                placeholder="What are their goals, dreams, or aspirations?"
                value={formData.goals}
                onChange={(e) => handleInputChange("goals", e.target.value)}
                className="min-h-[80px] w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 border border-gray-300 hover:bg-gray-50 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-md"
          >
            {editingAudience ? "Update Audience" : "Create Audience"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomAudienceModal; 